/**
 * Data access for the "Your Community" dashboard (/my-community).
 *
 * `referral-metric-service`'s ranking/gamification vertical (Slice 3 of
 * .claude/contracts/referral-leaderboard-program.md) is now merged — `GET /referrals/me` returns
 * real `leaderboard_score`/`ranks`/`badges` instead of Slice 2's zero/null/empty placeholders, and
 * `GET /referrals/community` now exists. Both TODOs below are closed: real `ApiService.get(...)`
 * calls through the `/api/referral-metric-service/` Nginx location (added ahead of this slice
 * specifically so it doesn't strip the caller's own Bearer token — see the Implementation Notes
 * entry for that fix), replacing the stub responses this file used to return.
 */

import ApiService from '../../infrastructure/api/ApiService';

export interface ReferralBadge {
  code: string;
  name: string;
  icon: string;
  season: string | null;
  earned_at: string;
}

export interface MyReferralMetrics {
  referral_code: string | null;
  share_url: string | null;
  referred_by: string | null;
  metrics: {
    referred_users_count: number;
    activated_referrals_count: number;
    verified_attendees_count: number;
    events_joined_count: number;
    events_attended_count: number;
    find_match_count: number;
    successful_match_count: number;
    community_membership_count: number;
  };
  leaderboard_score: number;
  ranks: { global: number; country: number | null; city: number | null };
  badges: ReferralBadge[];
}

export interface CommunitySummary {
  members: number;
  active: number;
  verified_attendees: number;
  new_this_week: number;
  events: number;
  recurring_members: number;
  active_referrers: number;
  community_score: number;
}

class ReferralMetricService {
  async getMyReferrals(): Promise<MyReferralMetrics> {
    const response = await ApiService.get<MyReferralMetrics>('/referral-metric-service/api/v1/referrals/me');
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to load your referral metrics');
    }
    return response.data;
  }

  async getCommunitySummary(): Promise<CommunitySummary> {
    const response = await ApiService.get<CommunitySummary>('/referral-metric-service/api/v1/referrals/community');
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to load your community summary');
    }
    return response.data;
  }
}

const referralMetricService = new ReferralMetricService();
export default referralMetricService;
